import { useEffect, useRef, useState } from 'react'

type UseHoverProps = {
  delay?: number
  defaultHover?: boolean
  dependProps?: string[]
  leaveDelay?: number
  useRefElement?: boolean
}
const useHover = <T extends Element>({
  delay = 0,
  defaultHover = false,
  dependProps = [] as string[],
  leaveDelay = 0,
  useRefElement = true,
}: UseHoverProps | undefined = {}) => {
  const ref = useRef<T>(null)
  const [isHovered, setHovered] = useState(defaultHover)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const setTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      setHovered(true)
    }, delay)

    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current)
    }
  }

  const handleMouseEnter = setTimer

  const handleMouseLeave = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current)
    }

    leaveTimeoutRef.current = setTimeout(() => {
      setHovered(false)
    }, leaveDelay)

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!useRefElement) return

    const node = ref.current
    if (node) {
      node.addEventListener('mouseenter', handleMouseEnter)
      node.addEventListener('mouseleave', handleMouseLeave)

      return () => {
        node.removeEventListener('mouseenter', handleMouseEnter)
        node.removeEventListener('mouseleave', handleMouseLeave)
      }
    }
  }, [ref.current, ...dependProps])

  return [
    ref,
    isHovered,
    setHovered,
    handleMouseLeave,
    handleMouseEnter,
  ] as const
}

export default useHover
